import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, IconButton, CircularProgress,
    Chip, Menu, MenuItem, ListItemIcon, ListItemText, InputAdornment, Divider, Tooltip, Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import api from '../../services/api';
import { isVisualizador } from '../../services/auth';
import { toastSuccess, toastWarning, toastError, alertValidacao, confirmAcao } from '../../services/alerts';
import { formatarCnpj, formatarCep, formatarTelefone, somenteDigitos } from '../../services/convenios';
import { buscarCep, cepCompleto, mensagemFalhaCep } from '../../services/cep';
import { consultarOperadora, mesclarConsulta, paraCamposDoFormulario } from '../../services/convenioConsulta';
import { explicarErro } from '../../services/erros';

const SectionTitle = ({ children, descricao }) => (
    <Box sx={{ mb: descricao ? 2 : 3 }}>
        <Typography variant="h6" sx={{ color: '#1ABB9C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 4, height: 24, bgcolor: '#1ABB9C', borderRadius: 1 }} />
            {children}
        </Typography>
        {descricao && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1.5 }}>
                {descricao}
            </Typography>
        )}
    </Box>
);

/**
 * Retorno da consulta, logo abaixo dos campos consultados.
 *
 * A ordem das mensagens é a ordem da gravidade: divergência impede o uso do
 * resultado, operadora inativa muda a decisão de cadastrar, falha parcial diz o
 * que não veio, e o conflito de campo diz o que foi preservado. Cada caso só
 * aparece quando existe — sem resultado, nada ocupa espaço na tela.
 */
function RetornoConsulta({ consulta }) {
    const { resultado, erro, conflitos, carregando } = consulta;
    if (carregando || (!resultado && !erro)) return null;

    if (erro) {
        return <Alert severity="error" sx={{ py: 0.5 }}>{erro}</Alert>;
    }

    const r = resultado;

    // Divergência: o resultado inteiro é inaproveitável, então é a única coisa
    // que aparece — mostrar os dados ao lado convidaria a usá-los.
    if (r.divergenciaCnpj) {
        return (
            <Alert severity="warning" sx={{ py: 0.5 }}>
                {r.divergenciaCnpj}
            </Alert>
        );
    }

    const achouAlgo = r.fonteAnsConsultada || r.fonteCnpjConsultada;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {achouAlgo && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: '1rem', color: '#1ABB9C' }} />
                    <Typography variant="caption" sx={{ color: '#1ABB9C', fontWeight: 600 }}>
                        {r.fonteAnsConsultada && r.fonteCnpjConsultada
                            ? 'Dados encontrados na ANS e na Receita'
                            : r.fonteAnsConsultada
                                ? 'Dados encontrados na ANS'
                                : 'Dados cadastrais encontrados'}
                    </Typography>
                </Box>
            )}

            {/* Inativa na ANS não impede cadastrar: a clínica pode ter guias em
                aberto com a operadora. Mas precisa ser dito. */}
            {r.ativaAns === false && (
                <Alert severity="warning" sx={{ py: 0.5, fontSize: '0.8rem' }}>
                    Esta operadora não consta como ativa na ANS. Isso é diferente do
                    campo <strong>Convênio ativo</strong> abaixo, que diz se a clínica
                    atende por ele.
                </Alert>
            )}

            {/* Falha de uma fonte só: o que veio da outra continua valendo. */}
            {r.avisoAns && !r.fonteAnsConsultada && (
                <Typography variant="caption" color="text.secondary">
                    {r.avisoAns}
                </Typography>
            )}
            {r.avisoCnpj && !r.fonteCnpjConsultada && (
                <Typography variant="caption" color="text.secondary">
                    {r.avisoCnpj}
                </Typography>
            )}

            {conflitos.length > 0 && (
                <Alert severity="info" sx={{ py: 0.5, fontSize: '0.8rem' }}>
                    Mantive o que já estava preenchido em{' '}
                    <strong>{conflitos.map(c => ROTULO_CAMPO[c.campo] || c.campo).join(', ')}</strong>.
                    A consulta trouxe valor diferente; ajuste à mão se quiser o da consulta.
                </Alert>
            )}
        </Box>
    );
}

const estadoInicial = () => ({
    nome: '', razaoSocial: '', registroAns: '', cnpj: '', telefone: '', email: '',
    modalidadeAns: '', situacaoAns: '',
    prazoRepasseDias: '', percentualRepasse: '', ativo: true, observacoes: '',
    cep: '', logradouro: '', numero: '', complemento: '', bairro: '', nomeCidade: '', ufEstado: '',
});

/** Rótulo de cada campo, para a mensagem de conflito dizer o que não mudou. */
const ROTULO_CAMPO = {
    nome: 'Nome', razaoSocial: 'Razão social', cnpj: 'CNPJ', registroAns: 'Registro ANS',
    telefone: 'Telefone', email: 'E-mail', cep: 'CEP', logradouro: 'Logradouro',
    numero: 'Número', complemento: 'Complemento', bairro: 'Bairro',
    nomeCidade: 'Cidade', ufEstado: 'UF',
};

export default function ConvenioForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const soLeitura = isVisualizador();

    const [form, setForm] = useState(estadoInicial());
    const [fetching, setFetching] = useState(!!id);
    const [saving, setSaving] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const fecharMenu = () => setMenuAnchor(null);

    /**
     * Estado da consulta às fontes públicas.
     *
     * `fonte` guarda qual campo disparou, para o indicador de carregamento e o
     * resultado aparecerem ao lado do campo consultado, e não numa barra que
     * bloqueia a tela inteira.
     */
    const [consulta, setConsulta] = useState({
        carregando: null,   // 'ans' | 'cnpj' | null
        resultado: null,    // resposta do backend
        erro: null,
        conflitos: [],
    });

    /**
     * Dispara a consulta e aplica o resultado.
     *
     * A mesclagem é decidida em `services/convenioConsulta`, com `edicao`
     * marcado quando o convênio já existe: ali os campos que identificam a
     * operadora ficam protegidos, porque trocá-los aponta o cadastro — com guias
     * e títulos atrás dele — para outra empresa.
     */
    const consultarFontes = async (fonte) => {
        const cnpjDigitos = somenteDigitos(form.cnpj);
        const registro = (form.registroAns || '').replace(/\D/g, '');

        if (fonte === 'ans' && !registro) {
            alertValidacao('Informe o registro ANS para consultar.');
            return;
        }
        if (fonte === 'cnpj' && cnpjDigitos.length !== 14) {
            alertValidacao('Informe os 14 dígitos do CNPJ para consultar.');
            return;
        }

        setConsulta({ carregando: fonte, resultado: null, erro: null, conflitos: [] });
        try {
            // Partindo da ANS, o CNPJ digitado vai junto para a validação cruzada.
            // Partindo do CNPJ, o registro não vai: ele é o que se quer descobrir.
            const dados = await consultarOperadora(
                fonte === 'ans'
                    ? { registroAns: registro, cnpj: cnpjDigitos || undefined }
                    : { cnpj: cnpjDigitos });

            const { valores, conflitos } = mesclarConsulta(
                form, paraCamposDoFormulario(dados), { edicao: !!id });

            if (Object.keys(valores).length > 0) {
                setForm(prev => ({
                    ...prev,
                    ...valores,
                    // CNPJ entra mascarado, como o campo espera.
                    ...(valores.cnpj ? { cnpj: formatarCnpj(valores.cnpj) } : {}),
                    ...(valores.telefone ? { telefone: formatarTelefone(valores.telefone) } : {}),
                    ...(valores.cep ? { cep: formatarCep(valores.cep) } : {}),
                }));
            }

            setConsulta({ carregando: null, resultado: dados, erro: null, conflitos });
        } catch (error) {
            setConsulta({
                carregando: null, resultado: null, conflitos: [],
                erro: explicarErro(error, 'consultar os dados da operadora').mensagem,
            });
        }
    };
    const [buscandoCep, setBuscandoCep] = useState(false);
    // Evita reconsultar o mesmo CEP a cada blur do campo.
    const [ultimoCepConsultado, setUltimoCepConsultado] = useState('');

    const carregar = useCallback(async () => {
        if (!id) return;
        try {
            const { data } = await api.get(`/planos-saude/${id}`);
            setForm({
                nome: data.nome || '',
                razaoSocial: data.razaoSocial || '',
                registroAns: data.registroAns || '',
                cnpj: data.cnpj ? formatarCnpj(data.cnpj) : '',
                modalidadeAns: data.modalidadeAns || '',
                situacaoAns: data.situacaoAns || '',
                telefone: data.telefone ? formatarTelefone(data.telefone) : '',
                email: data.email || '',
                prazoRepasseDias: data.prazoRepasseDias ?? '',
                percentualRepasse: data.percentualRepasse ?? '',
                ativo: data.ativo ?? true,
                observacoes: data.observacoes || '',
                cep: data.endereco?.cep ? formatarCep(data.endereco.cep) : '',
                logradouro: data.endereco?.logradouro || '',
                numero: data.endereco?.numero || '',
                complemento: data.endereco?.complemento || '',
                bairro: data.endereco?.bairro || '',
                nomeCidade: data.endereco?.cidade?.nome || '',
                ufEstado: data.endereco?.cidade?.estado?.uf || '',
            });
            // CEP que já está gravado não precisa ir ao ViaCEP de novo (e não
            // deve sobrescrever um endereço corrigido à mão).
            setUltimoCepConsultado(somenteDigitos(data.endereco?.cep));
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setFetching(false);
        }
    }, [id]);

    useEffect(() => { carregar(); }, [carregar]);

    const set = (campo, transform) => (e) => {
        const valor = transform ? transform(e.target.value) : e.target.value;
        setForm(prev => ({ ...prev, [campo]: valor }));
    };

    /**
     * Preenche o endereço pelo CEP (ViaCEP). Dispara ao completar os 8 dígitos e
     * também no blur, para o caso de colar um CEP já formatado.
     *
     * O que o usuário digitou prevalece: logradouro, bairro, cidade e UF só são
     * sobrescritos quando estão vazios ou quando o CEP mudou de verdade — assim
     * uma correção manual não é desfeita por um blur no campo.
     */
    const consultarCep = useCallback(async (valorCep, { forcar = false } = {}) => {
        if (soLeitura) return;
        const digitos = somenteDigitos(valorCep);
        if (!cepCompleto(digitos)) return;
        if (!forcar && digitos === ultimoCepConsultado) return;

        setBuscandoCep(true);
        try {
            const resultado = await buscarCep(digitos);
            setUltimoCepConsultado(digitos);
            if (!resultado.ok) {
                if (resultado.motivo === 'nao_encontrado') toastWarning(mensagemFalhaCep(resultado.motivo));
                else toastError(mensagemFalhaCep(resultado.motivo));
                return;
            }
            const e = resultado.endereco;
            setForm(prev => ({
                ...prev,
                logradouro: forcar || !prev.logradouro ? e.logradouro : prev.logradouro,
                bairro: forcar || !prev.bairro ? e.bairro : prev.bairro,
                nomeCidade: forcar || !prev.nomeCidade ? e.localidade : prev.nomeCidade,
                ufEstado: forcar || !prev.ufEstado ? e.uf : prev.ufEstado,
                complemento: prev.complemento || e.complemento,
            }));
        } finally {
            setBuscandoCep(false);
        }
    }, [soLeitura, ultimoCepConsultado]);

    const handleCepChange = (e) => {
        const mascarado = formatarCep(e.target.value);
        setForm(prev => ({ ...prev, cep: mascarado }));
        if (cepCompleto(mascarado)) consultarCep(mascarado);
    };

    /**
     * Cidade e UF andam juntas: o back resolve a cidade por nome + UF, então
     * mandar só uma das duas apagaria o endereço inteiro em silêncio.
     */
    const montarEndereco = () => {
        const cep = somenteDigitos(form.cep);
        const temAlgo = cep || form.logradouro || form.numero || form.bairro || form.nomeCidade;
        if (!temAlgo) return null;
        return {
            cep: cep || null,
            logradouro: form.logradouro || null,
            numero: form.numero || null,
            complemento: form.complemento || null,
            bairro: form.bairro || null,
            nomeCidade: form.nomeCidade || null,
            ufEstado: form.ufEstado ? form.ufEstado.toUpperCase() : null,
        };
    };

    const handleSalvar = async () => {
        if (!form.nome.trim()) { alertValidacao('O nome do convênio é obrigatório.'); return; }
        const cnpj = somenteDigitos(form.cnpj);
        if (cnpj && cnpj.length !== 14) { alertValidacao('CNPJ incompleto.'); return; }
        if (form.nomeCidade && !form.ufEstado) { alertValidacao('Informe a UF da cidade.'); return; }
        if (form.ufEstado && !form.nomeCidade) { alertValidacao('Informe a cidade.'); return; }

        setSaving(true);
        try {
            const payload = {
                nome: form.nome.trim(),
                razaoSocial: form.razaoSocial?.trim() || null,
                registroAns: form.registroAns || null,
                cnpj: cnpj || null,
                // Vêm da consulta e seguem como o usuário revisou: salvar não
                // reconsulta nada.
                modalidadeAns: form.modalidadeAns || null,
                situacaoAns: form.situacaoAns || null,
                telefone: somenteDigitos(form.telefone) || null,
                email: form.email || null,
                prazoRepasseDias: form.prazoRepasseDias !== '' ? Number(form.prazoRepasseDias) : null,
                percentualRepasse: form.percentualRepasse !== '' ? Number(form.percentualRepasse) : null,
                ativo: form.ativo,
                observacoes: form.observacoes || null,
                endereco: montarEndereco(),
            };
            if (id) {
                await api.put(`/planos-saude/${id}`, payload);
                toastSuccess('Convênio atualizado');
                carregar();
            } else {
                const { data } = await api.post('/planos-saude', payload);
                toastSuccess('Convênio cadastrado');
                navigate(`/convenios/${data.id_plano_saude}/procedimentos`);
            }
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setSaving(false);
        }
    };

    const handleAlterarStatus = async () => {
        fecharMenu();
        const inativando = form.ativo;
        const ok = await confirmAcao({
            title: inativando ? 'Inativar convênio' : 'Reativar convênio',
            text: inativando
                ? 'O convênio deixa de aceitar novas carteirinhas, guias e lotes. O histórico é mantido. Continuar?'
                : 'Reativar este convênio?',
            confirmButtonText: inativando ? 'Inativar' : 'Reativar',
            icon: inativando ? 'warning' : 'question',
            danger: inativando,
        });
        if (!ok) return;
        try {
            await api.patch(`/planos-saude/${id}/${inativando ? 'inativar' : 'reativar'}`);
            toastSuccess(inativando ? 'Convênio inativado' : 'Convênio reativado');
            carregar();
        } catch {
            /* erro exibido pelo handler global */
        }
    };

    if (fetching) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate('/convenios')} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333', flexGrow: 1 }}>
                    {soLeitura ? 'Visualizar convênio' : id ? 'Editar convênio' : 'Novo convênio'}
                </Typography>
                {id && (
                    <>
                        <Chip label={form.ativo ? 'Ativo' : 'Inativo'} color={form.ativo ? 'success' : 'default'} size="small" />
                        <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                            <MoreVertIcon />
                        </IconButton>
                        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={fecharMenu}>
                            <MenuItem onClick={() => { fecharMenu(); navigate(`/convenios/${id}/procedimentos`); }}>
                                <ListItemIcon><PriceChangeIcon fontSize="small" color="action" /></ListItemIcon>
                                <ListItemText>Tabela de valores</ListItemText>
                            </MenuItem>
                            {!soLeitura && (
                                <MenuItem onClick={handleAlterarStatus}>
                                    <ListItemIcon>
                                        {form.ativo ? <BlockIcon fontSize="small" color="warning" /> : <CheckCircleIcon fontSize="small" color="success" />}
                                    </ListItemIcon>
                                    <ListItemText>{form.ativo ? 'Inativar convênio' : 'Reativar convênio'}</ListItemText>
                                </MenuItem>
                            )}
                        </Menu>
                    </>
                )}
            </Box>

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <SectionTitle descricao="Identificação da operadora. O registro ANS e o CNPJ, quando informados, não podem repetir em outro convênio.">
                    Dados do convênio
                </SectionTitle>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField sx={{ flex: 2, minWidth: 220 }} label="Nome *" value={form.nome}
                            onChange={set('nome')} disabled={soLeitura} />
                        <TextField sx={{ flex: 2, minWidth: 220 }} label="Razão social" value={form.razaoSocial}
                            onChange={set('razaoSocial')} disabled={soLeitura}
                            helperText="Nome registrado; o Nome acima é o de uso na clínica" />
                    </Box>

                    {/* Registro ANS e CNPJ: cada um com a própria busca. Uma
                        alimenta a outra no servidor, então consultar por qualquer
                        um dos dois traz o conjunto. */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField sx={{ flex: 1, minWidth: 220 }} label="Registro ANS" value={form.registroAns}
                            onChange={set('registroAns')} disabled={soLeitura}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); consultarFontes('ans'); } }}
                            InputProps={{
                                endAdornment: !soLeitura && (
                                    <InputAdornment position="end">
                                        <Tooltip title="Consultar operadora na ANS">
                                            <span>
                                                <IconButton size="small" edge="end"
                                                    onClick={() => consultarFontes('ans')}
                                                    disabled={!!consulta.carregando}>
                                                    {consulta.carregando === 'ans'
                                                        ? <CircularProgress size={18} />
                                                        : <SearchIcon fontSize="small" />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                            helperText={consulta.carregando === 'ans' ? 'Consultando dados da ANS...' : ' '} />

                        <TextField sx={{ flex: 1, minWidth: 220 }} label="CNPJ" value={form.cnpj}
                            onChange={set('cnpj', formatarCnpj)} disabled={soLeitura}
                            placeholder="00.000.000/0000-00"
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); consultarFontes('cnpj'); } }}
                            InputProps={{
                                endAdornment: !soLeitura && (
                                    <InputAdornment position="end">
                                        <Tooltip title="Consultar dados cadastrais do CNPJ">
                                            <span>
                                                <IconButton size="small" edge="end"
                                                    onClick={() => consultarFontes('cnpj')}
                                                    disabled={!!consulta.carregando}>
                                                    {consulta.carregando === 'cnpj'
                                                        ? <CircularProgress size={18} />
                                                        : <SearchIcon fontSize="small" />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                            helperText={consulta.carregando === 'cnpj' ? 'Consultando dados do CNPJ...' : ' '} />
                    </Box>

                    <RetornoConsulta consulta={consulta} />

                    {/* Dados regulatórios: só leitura, porque vêm da ANS. Editá-los
                        à mão afirmaria situação regulatória sem fonte. */}
                    {(form.modalidadeAns || form.situacaoAns) && (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TextField sx={{ flex: 2, minWidth: 220 }} label="Modalidade ANS"
                                value={form.modalidadeAns} disabled
                                helperText="Classificação da operadora na ANS" />
                            <TextField sx={{ flex: 1, minWidth: 160 }} label="Situação na ANS"
                                value={form.situacaoAns} disabled
                                helperText="Situação regulatória, diferente de o convênio estar ativo aqui" />
                        </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField sx={{ flex: 1, minWidth: 160 }} label="Telefone" value={form.telefone}
                            onChange={set('telefone', formatarTelefone)} disabled={soLeitura} />
                        <TextField sx={{ flex: 2, minWidth: 220 }} label="E-mail" value={form.email}
                            onChange={set('email')} disabled={soLeitura} />
                    </Box>

                    <Divider />

                    <SectionTitle descricao="Prazo contratual de repasse define o vencimento do título gerado no envio de cada lote. Sem prazo cadastrado, o sistema usa 30 dias.">
                        Repasse
                    </SectionTitle>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField sx={{ flex: 1, minWidth: 180 }} type="number" label="Prazo de repasse (dias)"
                            value={form.prazoRepasseDias} onChange={set('prazoRepasseDias')} disabled={soLeitura}
                            inputProps={{ min: 0 }} />
                        <TextField sx={{ flex: 1, minWidth: 180 }} type="number" label="Percentual de repasse"
                            value={form.percentualRepasse} onChange={set('percentualRepasse')} disabled={soLeitura}
                            inputProps={{ min: 0, max: 100, step: '0.01' }}
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            helperText="Deixe vazio quando o repasse segue a tabela de valores por procedimento." />
                    </Box>

                    <Divider />

                    <SectionTitle descricao="Opcional. Cidade e UF andam juntas: a cidade é resolvida por nome + UF.">
                        Endereço
                    </SectionTitle>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField sx={{ flex: 1, minWidth: 160 }} label="CEP" value={form.cep}
                            onChange={handleCepChange}
                            onBlur={() => consultarCep(form.cep)}
                            disabled={soLeitura}
                            placeholder="00000-000"
                            helperText={buscandoCep ? 'Consultando ViaCEP...' : 'Preenche o endereço automaticamente.'}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {buscandoCep
                                            ? <CircularProgress size={18} />
                                            : (
                                                <Tooltip title="Buscar endereço pelo CEP">
                                                    <span>
                                                        <IconButton size="small" edge="end" disabled={soLeitura || !cepCompleto(form.cep)}
                                                            onClick={() => consultarCep(form.cep, { forcar: true })}>
                                                            <SearchIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                    </InputAdornment>
                                ),
                            }} />
                        <TextField sx={{ flex: 3, minWidth: 220 }} label="Logradouro" value={form.logradouro}
                            onChange={set('logradouro')} disabled={soLeitura} />
                        <TextField sx={{ flex: 1, minWidth: 100 }} label="Número" value={form.numero}
                            onChange={set('numero')} disabled={soLeitura} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField sx={{ flex: 1, minWidth: 160 }} label="Complemento" value={form.complemento}
                            onChange={set('complemento')} disabled={soLeitura} />
                        <TextField sx={{ flex: 1, minWidth: 160 }} label="Bairro" value={form.bairro}
                            onChange={set('bairro')} disabled={soLeitura} />
                        <TextField sx={{ flex: 1, minWidth: 160 }} label="Cidade" value={form.nomeCidade}
                            onChange={set('nomeCidade')} disabled={soLeitura} />
                        <TextField sx={{ width: 100 }} label="UF" value={form.ufEstado}
                            onChange={set('ufEstado', (v) => v.toUpperCase().slice(0, 2))} disabled={soLeitura} />
                    </Box>

                    <TextField label="Observações" value={form.observacoes} onChange={set('observacoes')}
                        disabled={soLeitura} multiline rows={2} inputProps={{ maxLength: 255 }} />
                </Box>

                {!soLeitura && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                        <Button variant="text" onClick={() => navigate('/convenios')}>Cancelar</Button>
                        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSalvar} disabled={saving}>
                            {saving ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
